import { useCallback, useMemo } from 'react';

const MAP_PROVIDERS = {
  AWS: 'aws',
  GOOGLE: 'google'
};

/**
 * Custom hook for handling marker positioning on different map providers
 * @param {React.RefObject} mapRef - Reference to the map instance
 * @param {React.RefObject} containerRef - Reference to the map container element
 * @param {Object} options - Configuration options
 * @param {number} options.duration - Animation duration in milliseconds
 * @param {Function} options.easing - Animation easing function
 * @returns {Object} Hook utilities and functions
 */
const useMarkerPositioning = (mapRef, containerRef, options = {}) => {
  const animationConfig = useMemo(
    () => ({
      duration: options.duration || 300,
      essential: true,
      easing: options.easing || ((t) => t * (2 - t))
    }),
    [options.duration, options.easing]
  );

  const calculatePixelOffset = useCallback((containerWidth, targetPixelX) => {
    return containerWidth / 2 - targetPixelX;
  }, []);

  const coordinateCalculators = useMemo(
    () => ({
      [MAP_PROVIDERS.GOOGLE]: (marker, pixelOffset, map) => {
        const markerPosition = marker.position;
        const mapContainer = map.getDiv();
        const containerWidth = mapContainer.offsetWidth;

        const targetPixelX = containerWidth / 2 - pixelOffset;
        const centerPixelX = containerWidth / 2;
        const pixelOffsetFromCenter = centerPixelX - targetPixelX;

        const projection = map.getProjection();
        const zoom = map.getZoom();

        // Convert marker LatLng to world coordinates
        const markerPoint = projection.fromLatLngToPoint(markerPosition);
        const scale = Math.pow(2, zoom);
        const worldCoordinate = new window.google.maps.Point(
          markerPoint.x * scale,
          markerPoint.y * scale
        );

        const newWorldX = worldCoordinate.x + pixelOffsetFromCenter;
        const newWorldPoint = new window.google.maps.Point(
          newWorldX / scale,
          markerPoint.y
        );

        return projection.fromPointToLatLng(newWorldPoint);
      },

      [MAP_PROVIDERS.AWS]: (marker, pixelOffset, map, containerWidth) => {
        const markerLatLng = marker.getLngLat();
        const bounds = map.getBounds();
        const lngSpan = bounds.getEast() - bounds.getWest();
        const lngOffset = (pixelOffset / containerWidth) * lngSpan;

        return [markerLatLng.lng + lngOffset, markerLatLng.lat];
      }
    }),
    []
  );

  /**
   * Align marker to specified position for both map providers
   * @param {string} mapProvider - MAP_PROVIDERS.GOOGLE or MAP_PROVIDERS.AWS
   * @param {Object} marker - Marker object (Google Maps marker or AWS marker coordinates)
   * @param {number} leftOffset - Distance from left edge where marker should be positioned
   */
  const calculateCenterRelativeToMarker = useCallback(
    (mapProvider, marker, leftOffset = 400) => {
      const map = mapRef.current;
      const mapContainer = containerRef.current || map?.getDiv();

      if (!map || !mapContainer) {
        return;
      }

      const containerWidth = mapContainer.offsetWidth;
      const pixelOffset = calculatePixelOffset(containerWidth, leftOffset);
      const calculateMapCenter = coordinateCalculators[mapProvider];

      if (!calculateMapCenter) {
        return;
      }

      const newCenter = calculateMapCenter(
        marker,
        pixelOffset,
        map,
        containerWidth
      );

      if (mapProvider === MAP_PROVIDERS.AWS) {
        map.panTo(newCenter, animationConfig);
        return;
      }

      map.panTo(newCenter);
    },
    [
      mapRef,
      containerRef,
      calculatePixelOffset,
      coordinateCalculators,
      animationConfig
    ]
  );

  return {
    calculateCenterRelativeToMarker,
    calculatePixelOffset,
    animationConfig,
    MAP_PROVIDERS
  };
};

export default useMarkerPositioning;
